import { useState, useEffect, useMemo, useRef } from "react";
import { Col, Row, Overlay, Tooltip } from "react-bootstrap";
import GridCard from "../../../components/shared/gridCard/GridCard";
import Datatable from "../../../components/shared/datatable/Datatable";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import Oven from "../../../images/oven_green.svg";
import { useSearchOvenDataQuery } from "../../processing/cooking/cookingApi";
import HumidityModal from "../rework/HumidityModal";
import EditHumidityModal from "../rework/EditHumidityModal";
import HumidSortingModal from "./HumidSortingModal";
import { useGetHumidSortingRecordQuery, useGetHumidSummaryQuery, useSearchHumidOvenDataQuery, useSearchHumidLogsQuery } from "./humidApi";
import Box from "../../../images/boxes.svg";
import CoolingRoom from "../../../images/cooling_room_orange.svg";

import { DEFAULT_DATETIME_FORMAT, DEFAULT_TIME_FORMAT } from "../../../helpers/constants";

import {
  convertToUserTimezone,
  formatDateAndTime,
  getCurrentDate,
  makePriorLotNumber,
} from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import InfoCard from "../../../components/shared/infocard/InfoCard";
import NoDataFound from "../../../components/shared/noDataFound/NoDataFound";
import HumidStatusModal from "./HumidStatusModal";
import RewarkPackedDataModal from "../rework/RewarkPackedDataModal";
import { useGetCheekStripPackedDataForReworkQuery, useProcessAccumulatedDataMutation } from "../rework/reworkApi";
import { useGetBaggingRecordQuery } from "../rework/reworkApi";
import BaggingRecordModal from "./BaggingRecordModal";


const Humid = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [addRecord, setAddRecord] = useState(false);
  const [editReworkData, setEditReworkData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tableFilters, setTableFilters] = useState({
    type: "all",
    status: "accumulated",
    entered_date: "",
  });
  const [page, setPage] = useState(1);
  const [ovenFilter, setOvenFilter] = useState({});
  const [tablePage, setTablePage] = useState(1);
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [activeBaggedTooltip, setActiveBaggedTooltip] = useState(null);
  const [isHoveringBaggedTooltip, setIsHoveringBaggedTooltip] = useState(false);
  const baggedTooltipRefs = useRef({});

  const { data: sortingRecordData } = useGetHumidSortingRecordQuery({ date });

  const pageLimit = 5;
  const humidOvenLogPerPage = 5;

  const { data: humidLogsData, isLoading: isHumidLogsLoading, isFetching: isHumidLogsFetching } = useSearchHumidLogsQuery({
    page: tablePage,
    per_page:pageLimit,
    ...tableFilters,
  });
  const [selectedBox, setSelectedBox] = useState(null);
  const [boxForm, setBoxForm] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState(null);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(date));
  const [addPackingData, setAddPackingData] = useState(false);
  const [packingModalMode, setPackingModalMode] = useState("");
  const [packingRecord, setPackingRecord] = useState(null);
  const [showPackingModal, setShowPackingModal] = useState(false);
  const [showBaggingModal, setShowBaggingModal] = useState(false);
  const [baggingModalMode, setBaggingModalMode] = useState("add");


  useEffect(() => {
    setLotNumber(makePriorLotNumber(date));
  }, [date]);

  const { data: summaryData } = useGetHumidSummaryQuery({ date });
  const { data: baggingRecordData } = useGetBaggingRecordQuery({ data_type: "humidity", date });
  const [processAccumulatedData, { isLoading: isProcessing }] = useProcessAccumulatedDataMutation();

  const handleProcessAll = async () => {
    try {
      await processAccumulatedData({ entity_type: "humidity" }).unwrap();
    } catch (error) {
      console.error("Error processing accumulated data:", error);
    }
  };

  const { data: packedData } = useGetCheekStripPackedDataForReworkQuery(
    { 
      date,
      data_source: "rework",
      data_type : "humidity"
    },
    { refetchOnMountOrArgChange: true }
  );

  const isToday = date === currentDate;
  const cheeksRecord = packedData?.data?.data?.find((item) => item.type === "cheeks" && item?.id);
  const stripsRecord = packedData?.data?.data?.find((item) => item.type === "strips" && item?.id);
  const hasCheeksRecord = Boolean(cheeksRecord);
  const hasStripsRecord = Boolean(stripsRecord);

  const [cheeksBoxesPackedData, setCheeksBoxesPackedData] = useState([]);
  const [stripsBoxesPackedData, setStripsBoxesPackedData] = useState([]);

  useEffect(() => {
    const items = packedData?.data?.data;
    setCheeksBoxesPackedData(
      items?.filter((item) => item.type === "cheeks").map((item) => [
        { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_one || 0}`, subvalue: _t("ten_pounds", "(10 pounds)") },
        { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_one || 0}`, subvalue: _t("ten_pounds", "(10 pounds)") },
        { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_before_lunch", "Seconds boxes Before Lunch"), value: item.second_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_after_lunch", "Seconds boxes After Lunch"), value: item.second_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("tapita_packed_before_lunch_20_pound", "Tapita packed before lunch"), value: item?.tapita_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("tapita_packed_after_lunch_20_pound", "Tapita packed after lunch"), value: item?.tapita_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_before_lunch_20_pound", "Pieces packed before lunch"), value: item?.pieces_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_after_lunch_20_pound", "Pieces packed after lunch"), value: item?.pieces_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("total_cheek_boxes_packed", "Total Cheek Boxes Packed"), value: item.boxes_packed },
        { label: _t("total_cheek_pallets_ready", "Total Cheek Pallets Ready"), value: item.pallets },
      ]) ?? []
    );
    setStripsBoxesPackedData(
      items?.filter((item) => item.type === "strips").map((item) => [
        { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_before_lunch", "Seconds boxes Before Lunch"), value: item.second_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_after_lunch", "Seconds boxes After Lunch"), value: item.second_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_before_lunch_20_pound", "Pieces packed before lunch"), value: item?.pieces_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_after_lunch_20_pound", "Pieces packed after lunch"), value: item?.pieces_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("total_strip_boxes_packed", "Total Strip Boxes Packed"), value: item.boxes_packed },
        { label: _t("total_strip_pallets_ready", "Total Strip Pallets Ready"), value: item.pallets },
      ]) ?? []
    );
  }, [packedData]);



  const { data: ovenData, isLoading: isOvenLoading, isFetching: isOvenFetching } = useSearchHumidOvenDataQuery({
    page,
    per_page:humidOvenLogPerPage,
    data_from: "rework",
    ...ovenFilter,
  });

  const handleAddData = () => {
    setAddRecord(true);
    setModalMode("add");
    setSelectedRecord(null);
    setShowModal(true);
  };

  const handleEdit = (data) => {
    setAddRecord(true);
    setModalMode(data.status === "cart_unloaded" ? "view" : "edit");
    setSelectedRecord(data);
    setShowModal(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const listItems = [
    {
      label: _t("total_humid_cheeks_kg", "Total Humid Cheeks Kg"),
      value: summaryData?.data.total_kg_cheeks_in_humidity || 0,
      icon: Oven,
      showDay:false
    },
    {
      label: _t("total_humid_strips_kg", "Total Humid Strips Kg"),
      value: summaryData?.data.total_kg_strips_in_humidity || 0,
      icon: Oven,
      showDay:false
    },
    {
      label: _t("processing.totalCartsInOven", "Total Carts in Oven"),
      value: summaryData?.data.total_carts_in_ovens || 0,
      icon: Oven,
      showDay:false
    },
    {
      label: _t("processing.cartsInCoolingRoom", "Carts in Cooling Room"),
      value: summaryData?.data.total_carts_in_cooling_room || 0,
      icon: CoolingRoom,
      showDay:false
    },
    // Total Cheeks Kg Bagged
    {
      label: _t("total_cheeks_kg_bagged", "Total Cheeks Kg Bagged"),
      value: summaryData?.data.total_cheeks_kg_bagged || 0,
      icon: Box,
      showDay:false
    },
    {
      label: _t("total_strips_kg_bagged", "Total Strips Kg Bagged"),
      value: summaryData?.data.total_strips_kg_bagged || 0,
      icon: Box,
      showDay:false
    },
    {
      label: _t("evaporation_prcnt", "Evaporation %"),
      value: summaryData?.data.evaporation_prcnt || 0,
      icon: Box,
      showDay:false
    },
  ];

  const handleFilterApply = (newFilters) => {
    setTableFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
    setTablePage(1);
  };

  const humidLogColumns = [
    {
      label: _t("humidity_table_date", "Date"),
      accessor: "created_at",
      cell: ({ value }) => convertToUserTimezone(value),
    },
    {
      label: _t("lot_no", "Lot Number"),
      accessor: "lot_number_id",
      cell: ({ value }) => value ? `#${value}` : "",
    },
    {
      label: _t("humidity_table_type", "Type"),
      accessor: "type",
      cell: ({ value }) =>
        value === "cheeks"
          ? _t("type_cheeks", "Cheeks")
          : _t("type_strips", "Strips"),
    },
    {
      label: _t("total_kg", "Total Kg"),
      accessor: "total_kg",
    },
    {
      label: _t("humidity_table_status", "Status"),
      accessor: "rework_status",
      cell: ({ row }) => (
        <span
          className={
            row.rework_status === "processed" ? "cmn_badge" : "cmn_badge inactive"
          }
        >
          {row.rework_status === "processed"
            ? _t("processed", "Processed")
            : _t("accumulated", "Accumulated")}
        </span>
      ),
    },
    // {
    //   label: _t("edit", "Edit"),
    //   accessor: "id",
    //   cell: ({ row }) => (
    //     <Button
    //       size="small"
    //       label={row.rework_status === "processed" ? _t("processed", "Processed") : _t("edit", "Edit")}
    //       onClick={() => {
    //         setStatusForm({
    //           ...row,
    //           created_at: convertToUserTimezone(row.created_at),
    //         });
    //         setShowStatusModal(true);
    //       }}
    //       className={row.rework_status === "processed" ? "borderRedBtn" : ""}
    //       disabled={row.rework_status === "processed"}
    //     />
    //   ),
    // },
  ];

  const calculateDuration = (start, end) => {
    if (!start || !end) return "";
    const diffMs = new Date(end) - new Date(start);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours} h ${minutes} min`;
  };

  const ovenColumns = [
    {
      label: _t("date", "Date"),
      accessor: "date_time_entered",
      cell: ({ value }) => convertToUserTimezone(value),
    },
    {
      label: _t("ovenNumber", "Oven Number"),
      accessor: "oven",
      cell: ({ value }) => value?.name || "",
    },
    {
      label: _t("time", "Time"),
      accessor: "time_entered",
      cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_TIME_FORMAT) : "",
    },
    {
      label: _t("cartNumber", "Carts Number"),
      accessor: "cart_number",
      cell: ({ value }) => value?.name || "",
    },
    {
      label: _t("type", "Type"),
      accessor: "type",
      cell: ({ value }) => value === "cheeks_tapita" ? "cheeks/tapita" : value,
    },
    {
      label: _t("treysCount", "Treys Count"),
      accessor: "treys_count",
    },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ row }) => {
        const { status, time_end } = row;
        const isOvenDone = status === "cooking" && time_end &&
              convertToUserTimezone(time_end, null, undefined, "valueOf") < convertToUserTimezone(null, null, undefined, "valueOf");
        
        return (
          <span className={
            isOvenDone ? "cmn_badge done"
            : status === "cart_unloaded" ? "cmn_badge done"
            : status === "cooling_room" ? "cmn_badge"
            : status === "cooking" ? "cmn_badge inactive"
            : "cmn_badge rejected"
          }>
            {isOvenDone ? _t("ovenDone", "Oven Done!")
              : status === "cooling_room" ? _t("coolingRoom", "Cooling Room")
              : status === "cooking" ? _t("cooking", "Cooking")
              : status === "cart_unloaded" ? _t("cart_unloaded", "Cart Unloaded")
              : _t("bagged", "Bagged")}
          </span>
        );
      },
    },
    {
      label: _t("ovenDuration", "Oven Duration"),
      accessor: "duration",
      cell: ({ row }) => calculateDuration(row.time_entered, row.time_end),
    },
    {
      label: _t("timeEnd", "Time End"),
      accessor: "time_end",
      cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_DATETIME_FORMAT) : "",
    },
    // {
    //   label: _t("bagged_weight", "Bagged Weight"),
    //   accessor: "status",
    //   cell: ({ row }) => {
    //     if (row.status !== "cart_unloaded") return <span style={{ color: "#ccc" }}>—</span>;
    //     const { type, cheeks_kg_bagged, strips_kg_bagged, tapita_kg_bagged } = row;

    //     const allBagged = [
    //       [_t("cheeks", "Cheeks"), cheeks_kg_bagged],
    //       [_t("strips", "Strips"), strips_kg_bagged],
    //       [_t("tapita", "Tapita"), tapita_kg_bagged],
    //     ];

    //     const pairs =
    //       type === "cheeks"        ? [[_t("cheeks", "Cheeks"), cheeks_kg_bagged]] :
    //       type === "strips"        ? [[_t("strips", "Strips"), strips_kg_bagged]] :
    //       type === "tapita"        ? [[_t("tapita", "Tapita"), tapita_kg_bagged]] :
    //       type === "cheeks/strips" ? [[_t("cheeks", "Cheeks"), cheeks_kg_bagged], [_t("strips", "Strips"), strips_kg_bagged]] :
    //       type === "cheeks_tapita" ? [[_t("cheeks", "Cheeks"), cheeks_kg_bagged], [_t("tapita", "Tapita"), tapita_kg_bagged]] :
    //       allBagged.filter(([, v]) => parseFloat(v) > 0);

    //     const total = pairs.reduce((sum, [, v]) => sum + (parseFloat(v) || 0), 0);

    //     if (!baggedTooltipRefs.current[row.id]) {
    //       baggedTooltipRefs.current[row.id] = { current: null };
    //     }

    //     return (
    //       <span className="d-flex align-items-center gap-1">
    //         <span
    //           ref={(el) => {
    //             if (!baggedTooltipRefs.current[row.id]) baggedTooltipRefs.current[row.id] = { current: null };
    //             baggedTooltipRefs.current[row.id].current = el;
    //           }}
    //           style={{
    //             background: "rgba(238,114,37,0.08)", border: "1px solid rgba(238,114,37,0.25)",
    //             borderRadius: "6px", padding: "2px 10px", fontSize: "12px",
    //             color: "#ee7225", fontWeight: 600, cursor: "pointer",
    //           }}
    //           onMouseEnter={() => setActiveBaggedTooltip(row.id)}
    //           onMouseLeave={() => setTimeout(() => { if (!isHoveringBaggedTooltip) setActiveBaggedTooltip(null); }, 100)}
    //           onClick={() => setActiveBaggedTooltip(activeBaggedTooltip === row.id ? null : row.id)}
    //         >
    //           {total} kg
    //         </span>
    //         <Overlay
    //           target={baggedTooltipRefs.current[row.id]?.current}
    //           show={activeBaggedTooltip === row.id}
    //           placement="top"
    //           rootClose
    //           onHide={() => { if (!isHoveringBaggedTooltip) setActiveBaggedTooltip(null); }}
    //         >
    //           {(props) => (
    //             <Tooltip
    //               id={`bagged-tooltip-${row.id}`}
    //               {...props}
    //               className="custom-tooltip"
    //               onMouseEnter={() => setIsHoveringBaggedTooltip(true)}
    //               onMouseLeave={() => { setIsHoveringBaggedTooltip(false); setActiveBaggedTooltip(null); }}
    //             >
    //               <div className="tooltip-content">
    //                 <div className="tooltip-header">{_t("bagged_weight_breakdown", "Bagged Weight")}</div>
    //                 <ul className="tooltip-list">
    //                   {pairs.map(([label, value]) => (
    //                     <li key={label}>
    //                       <span>• {label}</span>
    //                       <span className="tooltip-amount">{parseFloat(value) || 0} kg</span>
    //                     </li>
    //                   ))}
    //                 </ul>
    //               </div>
    //             </Tooltip>
    //           )}
    //         </Overlay>
    //       </span>
    //     );
    //   },
    // },
    {
      label: _t("humidity_table_overview", "Overview"),
      accessor: "status",
      cell: ({ row }) => {
        if (row.status === "cart_unloaded") return null;
        return (
          <Button
            size="small"
            label={_t("edit", "Edit")}
            onClick={() => handleEdit(row)}
          />
        );
      },
    },
  ];

  const handleOpenBoxModal = (box) => {
    setSelectedBox(box);
    setBoxForm(box ? { ...box } : null);
    setShowBoxModal(true);
  };

  const handleCheeksPacked = () => {
    if (!isToday && hasCheeksRecord) return;
    setAddPackingData(true);
    setPackingModalMode(hasCheeksRecord ? "editCheeks" : "cheeks");
    setPackingRecord(hasCheeksRecord ? cheeksRecord : null);
    setShowPackingModal(true);
  };

  const handleStripsPacked = () => {
    if (!isToday && hasStripsRecord) return;
    setAddPackingData(true);
    setPackingModalMode(hasStripsRecord ? "editStrips" : "strips");
    setPackingRecord(hasStripsRecord ? stripsRecord : null);
    setShowPackingModal(true);
  };



  return (
    <div>
      {showBaggingModal && (
        <BaggingRecordModal
          show={showBaggingModal}
          setShow={setShowBaggingModal}
          record={baggingRecordData?.data?.id ? baggingRecordData.data : null}
          mode={baggingRecordData?.data?.id ? "edit" : "add"}
          data_type="humidity"
        />
      )}

      {/* Add Humid Oven Log Data */}
      
      {addRecord && (  
        <HumidityModal
          show={showModal}
          setShow={setAddRecord}
          mode={modalMode}
          record={selectedRecord}
          type_name={tableFilters.type === "all" ? "" : tableFilters.type}
          date={date}
        />
      )}

      {editReworkData && (
        <EditHumidityModal
          show={showModal}
          setShow={setEditReworkData}
          mode={modalMode}
          record={selectedRecord}
        />
      )}

      {showBoxModal && (
        <HumidSortingModal
          show={showBoxModal}
          setShow={setShowBoxModal}
          record={boxForm}
          mode={boxForm?.id ? "edit" : "add"}
        />
      )}

      {addPackingData && (
        <RewarkPackedDataModal
          show={showPackingModal}
          setShow={setAddPackingData}
          mode={packingModalMode}
          record={packingRecord}
          lot_number_id={lotNumber}
          data_source="rework"
          data_type="humidity"
        />
      )}

      {showStatusModal && (
        <HumidStatusModal
          show={showStatusModal}
          setShow={setShowStatusModal}
          statusForm={statusForm}
          setStatusForm={setStatusForm}
        />
      )}

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">
            {_t("humidity_overview_title", "Humidity Room Overview")}
          </h6>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              value={list.value}
              icon={list.icon}
              day={
                currentDate === date
                  ? _t("common_today", "Today")
                  : formatDateAndTime(date, PROJECT_DATE_FORMAT)
              }
              showDay={list.showDay}
            />
          ))}
        </ul>
      </div>

      <div className="my-3" />
      <div className="humid_logs h-100 pt-2">
        <Datatable
          title={_t("humid_rework_logs", "Humid Rework Logs")}
          columns={humidLogColumns}
          tableData={humidLogsData?.data}
          isLoading={isHumidLogsLoading || isHumidLogsFetching}
          showPegination={true}
          page={tablePage}
          onPageChange={setTablePage}
          showCount={true}
          showFilter={handleFilterApply}
          showFilterButton={true}
          filterModule="humid-logs-filter"
          filters={tableFilters}
          pagination
          pageLimit={pageLimit}
          totalRecordsTitle={_t("number_of_humid_logs", "Number of Humid Logs")}
          buttons={[
            {
              label: isProcessing ? _t("processing", "Processing...") : _t("process_all_accumulated", "Process All Accumulated"),
              onClick: handleProcessAll,
              // className: "borderRedBtn",
            },
          ]}
        />
      </div>

      <div className="my-3" />      
        <div className="humid_logs h-100 pt-2">
        <Datatable
          title={_t("humidity_oven_table_title", "Humidity Oven Logs")}
          columns={ovenColumns}
          tableData={ovenData?.data}
          buttons={[
            {
              label: _t("add_oven_data", "Add Oven Data"),
              onClick: handleAddData,
              icon: PlusSvg,
            },
          ]}
          showPegination={true}
          pagination
          page={page}
          onPageChange={handlePageChange}
          showCount={true}
          showFilter={setOvenFilter}
          showFilterButton={true}
          pageLimit={humidOvenLogPerPage}
          filterModule="oven-filter"
          filters={ovenFilter}
          isLoading={isOvenLoading || isOvenFetching}
        />
        </div>
      {/* PACKING & SORTING BOXES  */}
      <div className="my-3" />
      

      {/* BAGGING RECORD */}
       <Row className="g-3">
        <Col lg={6}>
          <div className="dash_card">
            <div className="d-flex justify-content-between align-items-center dash_header">
              <h3 className="mb-0">{_t("bagging_log", "Bagging Log")}</h3>
              <Button
                size="xs"
                label={baggingRecordData?.data?.id ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                icon={PlusSvg}
                onClick={() => setShowBaggingModal(true)}
              />
            </div>
            <div className="p-3">
              {baggingRecordData?.data?.id ? (
                <Row>
                  <Col lg={6}><InfoCard label={_t("start_time", "Start Time")} value={baggingRecordData.data.start_time ? convertToUserTimezone(baggingRecordData.data.start_time) : ""} /></Col>
                  <Col lg={6}><InfoCard label={_t("end_time", "End Time")} value={baggingRecordData.data.end_time ? convertToUserTimezone(baggingRecordData.data.end_time) : ""} /></Col>
                  <Col lg={6}><InfoCard label={_t("cheeks_weight_before", "Cheeks Weight Before")} value={baggingRecordData.data.cheeks_weight_before} /></Col>
                  <Col lg={6}><InfoCard label={_t("cheeks_weight_after", "Cheeks Weight After")} value={baggingRecordData.data.cheeks_weight_after} /></Col>
                  <Col lg={6}><InfoCard label={_t("strips_weight_before", "Strips Weight Before")} value={baggingRecordData.data.strips_weight_before} /></Col>
                  <Col lg={6}><InfoCard label={_t("strips_weight_after", "Strips Weight After")} value={baggingRecordData.data.strips_weight_after} /></Col>
                  <Col lg={6}><InfoCard label={_t("total_weight_before", "Total Weight Before")} value={baggingRecordData.data.total_weight_before} /></Col>
                  <Col lg={6}><InfoCard label={_t("total_weight_after", "Total Weight After")} value={baggingRecordData.data.total_weight_after} /></Col>
                  <Col lg={6}><InfoCard label={_t("total_evaporation", "Total Evaporation (%)")} value={baggingRecordData.data.total_evaporation} /></Col>
                </Row>
              ) : (
                <NoDataFound />
              )}
            </div>
          </div>
        </Col>
        <Col lg={6}>
          <Row className="g-3">
            <Col lg={12}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <h3 className="mb-0">{_t("cheeks_strips_sorting_data", "Cheeks/Strip Sorting Data")}</h3>
                  <Button
                    size="xs"
                    label={sortingRecordData?.data?.id ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                    icon={PlusSvg}
                    onClick={() => handleOpenBoxModal(sortingRecordData?.data?.id ? sortingRecordData.data : null)}
                  />
                </div>
                <div className="p-3">
                  <Row>
                    {sortingRecordData?.data?.id ? (
                      (() => {
                        const box = sortingRecordData.data;
                        return (
                          <>
                            <Col lg={6}><InfoCard label={_t("prime_cheeks_kg", "Prime Cheeks Kg")} value={box.prime_cheeks_kg} /></Col>
                            <Col lg={6}><InfoCard label={_t("second_cheeks_kg", "Second Cheeks Kg")} value={box.second_cheeks_kg} /></Col>
                            <Col lg={6}><InfoCard label={_t("prime_strips_kg", "Prime Strips Kg")} value={box.prime_strips_kg} /></Col>
                            <Col lg={6}><InfoCard label={_t("second_strips_kg", "Second Strips Kg")} value={box.second_strips_kg} /></Col>
                            <Col lg={6}><InfoCard label={_t("tapita_kg", "Tapita Kg")} value={box.tapita_kg} /></Col>
                            <Col lg={6}><InfoCard label={_t("pieces_kg", "Pieces Kg")} value={box.pieces_kg} /></Col>
                            <Col lg={6}><InfoCard label={_t("trash", "Trash")} value={box.rejected_kg} /></Col>
                          </>
                        );
                      })()
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </Col>
        {/*  Packing Box — Cheeks */}
        <Col lg={6}>
          <div className="dash_card h-100">
            <div className="d-flex justify-content-between align-items-center dash_header">
              <h3 className="mb-0">{_t("cheeks_packing_logs", "Cheeks Packing Logs")}</h3>
              {(isToday || !hasCheeksRecord) && (
                <Button
                  size="xs"
                  label={hasCheeksRecord ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                  icon={PlusSvg}
                  onClick={handleCheeksPacked}
                />
              )}
            </div>
            <div className="p-3">
              <Row>
                {cheeksBoxesPackedData?.length > 0 ? (
                  cheeksBoxesPackedData.map((data, index) => (
                    <Row key={index}>
                      {data.map((item, itemIndex) => (
                        <Col lg={6} key={`${item.label}-${itemIndex}`}>
                          <InfoCard label={item.label} value={item.value} subvalue={item?.subvalue ?? ""} />
                        </Col>
                      ))}
                    </Row>
                  ))
                ) : (
                  <NoDataFound />
                )}
              </Row>
            </div>
          </div>
        </Col>

        {/*  Packing Box — Strips */}
        <Col lg={6}>
          <div className="dash_card h-100">
            <div className="d-flex justify-content-between align-items-center dash_header">
              <h3 className="mb-0">{_t("strips_packing_logs", "Strips Packing Logs")}</h3>
              {(isToday || !hasStripsRecord) && (
                <Button
                  size="xs"
                  label={hasStripsRecord ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                  icon={PlusSvg}
                  onClick={handleStripsPacked}
                />
              )}
            </div>
            <div className="p-3">
              <Row>
                {stripsBoxesPackedData?.length > 0 ? (
                  stripsBoxesPackedData.map((data, index) => (
                    <Row key={index}>
                      {data.map((item, itemIndex) => (
                        <Col lg={6} key={`${item.label}-${itemIndex}`}>
                          <InfoCard label={item.label} value={item.value} subvalue={item?.subvalue ?? ""} />
                        </Col>
                      ))}
                    </Row>
                  ))
                ) : (
                  <NoDataFound />
                )}
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Humid;
